
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

// Hardcoded admin credentials
const ADMIN_EMAIL = 'admin@university.edu'
const ADMIN_PASSWORD = 'admin123'

const AdminLogin = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    // Check if admin is already logged in - only run once
    const checkAdminAuth = async () => {
      try {
        const isAdminLoggedIn = localStorage.getItem('adminSession')
        if (isAdminLoggedIn) {
          // Verify the session is still valid with Supabase
          const { data: { session } } = await supabase.auth.getSession()
          if (session) {
            navigate('/admin/dashboard')
            return
          } else {
            // Clear invalid session
            localStorage.removeItem('adminSession')
          }
        }
      } catch (error) {
        console.error('Error checking admin auth:', error)
        localStorage.removeItem('adminSession')
      } finally {
        setIsCheckingAuth(false)
      }
    }

    checkAdminAuth()
  }, []) // Remove navigate dependency to prevent loops

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      // Keep the same demo credentials UX, but authenticate with Supabase
      if (email !== ADMIN_EMAIL || password !== ADMIN_PASSWORD) {
        toast({
          title: 'Admin sign in failed',
          description: 'Invalid admin credentials. Please check your email and password.',
          variant: 'destructive',
        })
        return
      }

      // Sign in with Supabase so RLS can authorize admin actions
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (signInError) {
        throw signInError
      }

      const userId = signInData.session?.user?.id
      if (!userId) {
        throw new Error('No session returned from Supabase')
      }

      // Verify this user is in the admins table
      const { data: adminRow, error: adminCheckError } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle()

      if (adminCheckError) throw adminCheckError
      if (!adminRow) {
        toast({
          title: 'Not an admin',
          description: 'This account is not authorized for the Admin Portal.',
          variant: 'destructive',
        })
        return
      }

      // Store admin session in localStorage for existing guard and proceed
      localStorage.setItem('adminSession', 'true')
      toast({ title: 'Welcome Admin!', description: 'You have been successfully signed in.' })
      navigate('/admin/dashboard')
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900 via-blue-800 to-blue-950 p-6">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900 via-blue-800 to-blue-950 p-6">
      <div className="w-full max-w-md">
        <Card className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-2">
              <img
                src="/dablylogofulltext-transparent.png"
                alt="Dably"
                className="h-10 w-auto drop-shadow-[0_4px_24px_rgba(59,130,246,0.35)]"
                loading="eager"
              />
            </div>

            <div className="text-xs text-white/60 text-center bg-white/5 p-3 rounded-lg border border-white/10">
              <strong>Demo Credentials:</strong><br />
              Email: admin@university.edu<br />
              Password: admin123
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your admin email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-black/30 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-blue-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-black/30 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-blue-400"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-400 text-white shadow-[0_6px_30px_rgba(59,130,246,0.4)]"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In as Admin'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AdminLogin
